/* eslint-disable react/react-in-jsx-scope */
import { useApi, alertApiRef } from '@backstage/core-plugin-api';
import { toastApiRef } from '@backstage/frontend-plugin-api';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControl from '@material-ui/core/FormControl';
import IconButton from '@material-ui/core/IconButton';
import InputLabel from '@material-ui/core/InputLabel';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Popover from '@material-ui/core/Popover';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import AccountTree from '@material-ui/icons/AccountTree';
import Android from '@material-ui/icons/Android';
import ArrowDropDown from '@material-ui/icons/ArrowDropDown';
import Check from '@material-ui/icons/Check';
import Close from '@material-ui/icons/Close';
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDown';
import type { Entity } from '@backstage/catalog-model';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { rithumAgentStartTaskEntityPermission } from '@internal/plugin-rithum-agent-common';
import { usePermission } from '@backstage/plugin-permission-react';
import { useEffect, useMemo, useState } from 'react';
import { rithumAgentApiRef } from './RithumAgentApi';
import { EntityCatalogPickerDialog } from './EntityCatalogPickerDialog';
import { useOptionalEntityFromRoute } from './useOptionalEntityFromRoute';
import type { AgentDescriptor, RithumAgentOptionsResponse } from './types';
import { normalizeAgentDescriptors } from './normalizeAgentDescriptors';
import {
  compactModelButtonLabel,
  dedupeModelsForPicker,
  normalizeModelForPickerState,
} from './modelPickerUtils';
import { FALLBACK_OPTIONS } from '@internal/plugin-rithum-agent-react';

const SOURCE_LOC = 'backstage.io/source-location';
const PROJECT_SLUG = 'github.com/project-slug';

const useStyles = makeStyles(theme => ({
  toolbarRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(1),
    flexWrap: 'wrap',
  },
  entityPillButton: {
    flex: 1,
    minWidth: 120,
    maxWidth: '100%',
    justifyContent: 'space-between',
    textTransform: 'none',
    borderColor: theme.palette.divider,
  },
  entityPillLabel: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textAlign: 'left',
  },
  branchPopoverBody: {
    padding: theme.spacing(2),
    minWidth: 280,
  },
  compactProvider: {
    marginTop: theme.spacing(1.5),
  },
}));

function sourceLocationForEntity(e: Entity): string | undefined {
  const ann = e.metadata.annotations ?? {};
  const sl = ann[SOURCE_LOC];
  if (sl) return sl;
  const slug = ann[PROJECT_SLUG];
  if (slug) return `https://github.com/${slug}`;
  return undefined;
}

type Props = {
  open: boolean;
  onClose: () => void;
};

export function RithumAgentPanel({ open, onClose }: Props) {
  const classes = useStyles();
  const rithumAgentApi = useApi(rithumAgentApiRef);
  const alertApi = useApi(alertApiRef);
  const toastApi = useApi(toastApiRef);
  const { entity } = useOptionalEntityFromRoute();

  const [options, setOptions] =
    useState<RithumAgentOptionsResponse>(FALLBACK_OPTIONS);
  const [agentProvider, setAgentProvider] = useState('cursor');
  const [model, setModel] = useState('auto');
  const [agentName, setAgentName] = useState(FALLBACK_OPTIONS.agentNames[0]);
  const [agentPrompt, setAgentPrompt] = useState('');
  const [pickedEntity, setPickedEntity] = useState<Entity | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [checkoutRef, setCheckoutRef] = useState('');
  const [branchNameOverride, setBranchNameOverride] = useState('');

  const [entityPickerOpen, setEntityPickerOpen] = useState(false);
  const [branchAnchor, setBranchAnchor] = useState<HTMLElement | null>(null);
  const [botMenuAnchor, setBotMenuAnchor] = useState<HTMLElement | null>(null);
  const [modelMenuAnchor, setModelMenuAnchor] = useState<HTMLElement | null>(
    null,
  );

  const catalogEntity = useMemo(
    () => pickedEntity ?? entity ?? null,
    [pickedEntity, entity],
  );

  const entityRefForPermission = useMemo(
    () => (catalogEntity ? stringifyEntityRef(catalogEntity) : undefined),
    [catalogEntity],
  );

  const { allowed: canStartTask, loading: permissionLoading } = usePermission({
    permission: rithumAgentStartTaskEntityPermission,
    resourceRef: entityRefForPermission,
  });

  const models = useMemo(() => {
    const by = options.modelsByProvider?.[agentProvider];
    return by?.length ? by : FALLBACK_OPTIONS.modelsByProvider.cursor;
  }, [options.modelsByProvider, agentProvider]);

  const modelsForPicker = useMemo(
    () => dedupeModelsForPicker(models),
    [models],
  );

  useEffect(() => {
    if (open) {
      setPickedEntity(entity ?? null);
    }
  }, [open, entity]);

  useEffect(() => {
    setModel(m => normalizeModelForPickerState(m, modelsForPicker));
  }, [modelsForPicker, agentProvider]);

  useEffect(() => {
    setAgentName(n =>
      options.agentNames.includes(n) ? n : options.agentNames[0],
    );
  }, [options.agentNames]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const opts = await rithumAgentApi.getOptions();
        const agents = normalizeAgentDescriptors(opts.agents);
        if (!cancelled && agents.length) {
          setOptions({
            agents,
            modelsByProvider:
              opts.modelsByProvider ?? FALLBACK_OPTIONS.modelsByProvider,
            agentNames: opts.agentNames?.length
              ? opts.agentNames
              : FALLBACK_OPTIONS.agentNames,
          });
        }
      } catch {
        /* keep fallbacks */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rithumAgentApi, open]);

  const resolvedSlFromPicker = catalogEntity
    ? sourceLocationForEntity(catalogEntity)
    : undefined;
  const displaySourceLocation = resolvedSlFromPicker ?? '';
  const entityPillTooltip = catalogEntity
    ? [
        stringifyEntityRef(catalogEntity),
        displaySourceLocation ? `Source: ${displaySourceLocation}` : '',
      ]
        .filter(Boolean)
        .join('\n')
    : 'Select a catalog entity';

  const entityPillText = catalogEntity
    ? stringifyEntityRef(catalogEntity)
    : 'Select entity';

  const handleSubmit = async () => {
    if (!catalogEntity) {
      alertApi.post({
        message:
          'Select a catalog entity (or open this dialog from an entity page).',
        severity: 'error',
      });
      return;
    }
    if (!canStartTask) {
      alertApi.post({
        message: 'Only entity owners can start an agent task for this project.',
        severity: 'error',
      });
      return;
    }

    setSubmitting(true);
    try {
      const entityRef = stringifyEntityRef(catalogEntity);
      const sl = sourceLocationForEntity(catalogEntity);
      if (!sl) {
        alertApi.post({
          message:
            'Selected entity has no backstage.io/source-location or github.com/project-slug.',
          severity: 'error',
        });
        setSubmitting(false);
        return;
      }

      const { workflowRunUrl } = await rithumAgentApi.startTask({
        agentProvider,
        model,
        agentName,
        agentPrompt,
        entityRef,
        sourceLocation: sl,
        checkoutRef: checkoutRef.trim() || undefined,
        branchName: branchNameOverride.trim() || undefined,
      });
      toastApi.post({
        title: 'Agent task started',
        status: 'success',
        timeout: 9000,
        ...(workflowRunUrl
          ? {
              links: [
                {
                  label: 'View workflow run',
                  href: workflowRunUrl,
                },
              ],
            }
          : {}),
      });
      onClose();
    } catch (e) {
      alertApi.post({
        message: `Failed to start agent task: ${(e as Error).message}`,
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const submitDisabled =
    submitting || permissionLoading || !catalogEntity || !canStartTask;

  const startButtonTitle = (() => {
    if (!catalogEntity) {
      return 'Select a catalog entity first';
    }
    if (permissionLoading) {
      return '';
    }
    if (!canStartTask) {
      return 'Only entity owners can start an agent task for this project';
    }
    return '';
  })();

  const startButton = (
    <Button
      color="primary"
      variant="contained"
      disabled={submitDisabled}
      onClick={handleSubmit}
    >
      {submitting ? 'Starting...' : 'Start Task'}
    </Button>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        aria-labelledby="rithum-agent-title"
      >
        <DialogTitle id="rithum-agent-title">
          Agent Task
          <IconButton
            aria-label="close"
            onClick={onClose}
            style={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {catalogEntity && !permissionLoading && !canStartTask && (
            <Typography variant="body2" color="error" paragraph>
              Only owners of this entity may start an agent task.
            </Typography>
          )}

          <TextField
            fullWidth
            margin="normal"
            placeholder='Describe what the agent should do. e.g. "Resolve DEVEX-123"'
            multiline
            minRows={4}
            value={agentPrompt}
            onChange={e => setAgentPrompt(e.target.value)}
            variant="outlined"
            inputProps={{ 'aria-label': 'Prompt' }}
          />

          <Box className={classes.toolbarRow}>
            <Tooltip title={entityPillTooltip}>
              <Button
                variant="outlined"
                className={classes.entityPillButton}
                size="small"
                endIcon={<KeyboardArrowDown />}
                onClick={() => setEntityPickerOpen(true)}
                aria-label="Select catalog entity"
              >
                <span className={classes.entityPillLabel}>
                  {entityPillText}
                </span>
              </Button>
            </Tooltip>

            <Tooltip title="Checkout ref and branch name">
              <IconButton
                size="small"
                aria-label="Branch and checkout options"
                onClick={e => setBranchAnchor(e.currentTarget)}
              >
                <AccountTree fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Agent name">
              <IconButton
                size="small"
                aria-label="Agent name"
                onClick={e => setBotMenuAnchor(e.currentTarget)}
              >
                <Android fontSize="small" />
              </IconButton>
            </Tooltip>

            <Button
              size="small"
              aria-label="Model"
              endIcon={<ArrowDropDown />}
              onClick={e => setModelMenuAnchor(e.currentTarget)}
              style={{ textTransform: 'none' }}
            >
              {compactModelButtonLabel(model)}
            </Button>
          </Box>

          <Popover
            open={Boolean(branchAnchor)}
            anchorEl={branchAnchor}
            onClose={() => setBranchAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <div className={classes.branchPopoverBody}>
              <TextField
                fullWidth
                size="small"
                margin="dense"
                label="Checkout ref"
                placeholder="main"
                value={checkoutRef}
                onChange={e => setCheckoutRef(e.target.value)}
                variant="outlined"
              />
              <TextField
                fullWidth
                size="small"
                margin="dense"
                label="Branch name (suffix)"
                placeholder="short-feature-name"
                value={branchNameOverride}
                onChange={e => setBranchNameOverride(e.target.value)}
                variant="outlined"
              />
            </div>
          </Popover>

          <Menu
            anchorEl={botMenuAnchor}
            open={Boolean(botMenuAnchor)}
            onClose={() => setBotMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            {options.agentNames.map(name => (
              <MenuItem
                key={name}
                selected={agentName === name}
                onClick={() => {
                  setAgentName(name);
                  setBotMenuAnchor(null);
                }}
              >
                {agentName === name ? (
                  <Check fontSize="small" style={{ marginRight: 8 }} />
                ) : (
                  <span
                    style={{
                      width: 24,
                      marginRight: 8,
                      display: 'inline-block',
                    }}
                  />
                )}
                {name}
              </MenuItem>
            ))}
          </Menu>

          <Menu
            anchorEl={modelMenuAnchor}
            open={Boolean(modelMenuAnchor)}
            onClose={() => setModelMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            {modelsForPicker.map(m => (
              <MenuItem
                key={m}
                selected={model === m}
                onClick={() => {
                  setModel(m);
                  setModelMenuAnchor(null);
                }}
              >
                {model === m ? (
                  <Check fontSize="small" style={{ marginRight: 8 }} />
                ) : (
                  <span
                    style={{
                      width: 24,
                      marginRight: 8,
                      display: 'inline-block',
                    }}
                  />
                )}
                {compactModelButtonLabel(m)}
              </MenuItem>
            ))}
          </Menu>

          <FormControl
            fullWidth
            margin="dense"
            variant="outlined"
            size="small"
            className={classes.compactProvider}
          >
            <InputLabel id="agent-provider-label">Agent Provider</InputLabel>
            <Select
              labelId="agent-provider-label"
              label="Agent Provider"
              value={agentProvider}
              onChange={e => setAgentProvider(String(e.target.value))}
              renderValue={selected => {
                const id = String(selected);
                const a = options.agents.find(x => x.id === id);
                return a?.label ?? id;
              }}
            >
              {options.agents.map((a: AgentDescriptor) =>
                a.disabled ? (
                  <Tooltip key={a.id} title="Coming soon">
                    <span>
                      <MenuItem value={a.id} disabled>
                        {a.label}
                      </MenuItem>
                    </span>
                  </Tooltip>
                ) : (
                  <MenuItem key={a.id} value={a.id}>
                    {a.label}
                  </MenuItem>
                ),
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          {startButtonTitle ? (
            <Tooltip title={startButtonTitle}>
              <span>{startButton}</span>
            </Tooltip>
          ) : (
            startButton
          )}
        </DialogActions>
      </Dialog>

      <EntityCatalogPickerDialog
        open={entityPickerOpen}
        onClose={() => setEntityPickerOpen(false)}
        value={pickedEntity}
        onChange={setPickedEntity}
      />
    </>
  );
}
